import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, IconButton, InputAdornment,
    Snackbar, Alert, Box
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Loader from 'ui-component/Loader';
import { useDispatch } from 'react-redux';
import { resetPassword } from 'redux/actions/PasswordAction'
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

const ResetPasswordDialog = ({ open, onClose }) => {
    const [showPassword, setShowPassword] = useState({});
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const dispatch = useDispatch();
    const empid = window.localStorage.getItem('empid');
    const formikRef = useRef(); //  for accessing Formik methods externally

    const handleSnackbarClose = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const togglePasswordVisibility = (field) => {
        setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    const getAdornment = (field) => (
        <InputAdornment position="end">
            <IconButton onClick={() => togglePasswordVisibility(field)} edge="end">
                {showPassword[field] ? <VisibilityOff /> : <Visibility />}
            </IconButton>
        </InputAdornment>
    );

    const initialValues = {
        CurrentPassword: '',
        NewPassword: '',
        ConfirmPassword: ''
    };

    const validationSchema = Yup.object({
        CurrentPassword: Yup.string().required('Current password is required'),
        NewPassword: Yup.string()
            .min(6, 'Password must be at least 6 characters')
            .required('New password is required'),
        ConfirmPassword: Yup.string()
            .oneOf([Yup.ref('NewPassword'), null], 'Passwords must match')
            .required('Confirm password is required')
    });

    const handleSubmit = (values, { resetForm }) => {
        setLoading(true);
    
        dispatch(
            resetPassword(
                {
                    empid,
                    currentPassword: values.CurrentPassword,
                    newPassword: values.NewPassword
                },
                (res) => {
                    setSnackbar({
                        open: true,
                        message: res.data,
                        severity: res.color === 'success' ? 'success' : 'error'
                    });
                    resetForm();
                    onClose();
                },
                (err) => {
                    setSnackbar({
                        open: true,
                        message: err.data,
                        severity: 'error'
                    });
                },
                () => setLoading(false)
            )
        );
    };

    //  to clear previous data in  Formik  form when dialog opens
    useEffect(() => {
        if (open && formikRef.current) {
            formikRef.current.resetForm();
        }
    }, [open]);

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>Reset Password</DialogTitle>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                        <Loader />
                    </Box>
                )}

                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ touched, errors }) => (
                        <Form>
                            <DialogContent>
                                <Field name="CurrentPassword">
                                    {({ field }) => (
                                        <TextField
                                            {...field}
                                            label="Current Password"
                                            type={showPassword.CurrentPassword ? 'text' : 'password'}
                                            fullWidth
                                            margin="dense"
                                            error={touched.CurrentPassword && Boolean(errors.CurrentPassword)}
                                            helperText={touched.CurrentPassword && errors.CurrentPassword}
                                            InputProps={{ endAdornment: getAdornment('CurrentPassword') }}
                                        />
                                    )}
                                </Field>

                                <Field name="NewPassword">
                                    {({ field }) => (
                                        <TextField
                                            {...field}
                                            label="New Password"
                                            type={showPassword.NewPassword ? 'text' : 'password'}
                                            fullWidth
                                            margin="dense"
                                            error={touched.NewPassword && Boolean(errors.NewPassword)}
                                            helperText={touched.NewPassword && errors.NewPassword}
                                            InputProps={{ endAdornment: getAdornment('NewPassword') }}
                                        />
                                    )}
                                </Field>

                                <Field name="ConfirmPassword">
                                    {({ field }) => (
                                        <TextField
                                            {...field}
                                            label="Confirm New Password"
                                            type={showPassword.ConfirmPassword ? 'text' : 'password'}
                                            fullWidth
                                            margin="dense"
                                            error={touched.ConfirmPassword && Boolean(errors.ConfirmPassword)}
                                            helperText={touched.ConfirmPassword && errors.ConfirmPassword}
                                            InputProps={{ endAdornment: getAdornment('ConfirmPassword') }}
                                        />
                                    )}
                                </Field>
                            </DialogContent>

                            <DialogActions>
                                <Button onClick={onClose}>Cancel</Button>
                                <Button type="submit" disabled={loading} variant="contained" color="primary">
                                    Update Password
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>

            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={snackbar.open}
                autoHideDuration={2000}
                onClose={handleSnackbarClose}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default ResetPasswordDialog;
